import { FC, ReactNode } from 'react';
import { useViewer } from '@annotorious/react';
import { Hand, Square } from 'lucide-react';

import { AnnotatorToolBarContainer } from '@/components/blocks/annotator/annotator-toolbar-container';
import type {
  AnnotatorLabels,
  AnnotatorMode,
} from '@/components/blocks/annotator/types';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

interface AnnotatorToolbarProps {
  mode: AnnotatorMode;
  onModeChange: (mode: AnnotatorMode) => void;
  readonly: boolean;
  labels: AnnotatorLabels;
  /** Extra controls rendered after the edit tools; hidden in readonly mode. */
  extras?: ReactNode;
}

export const AnnotatorToolbar: FC<AnnotatorToolbarProps> = ({
  mode,
  onModeChange,
  readonly,
  labels,
  extras,
}: AnnotatorToolbarProps) => {
  const viewer = useViewer();

  const zoom = (factor: number) => {
    viewer.viewport.zoomBy(factor);
    viewer.viewport.applyConstraints();
  };

  const fit = () => {
    viewer.viewport.goHome();
  };

  if (readonly) {
    return (
      <AnnotatorToolBarContainer
        fit={fit}
        zoom={zoom}
        labels={labels}
        mode={mode}
      />
    );
  }

  return (
    <AnnotatorToolBarContainer
      fit={fit}
      zoom={zoom}
      labels={labels}
      mode={mode}>
      <Button
        type='button'
        variant={mode === 'pan' ? 'default' : 'outline'}
        size='icon'
        aria-label={labels.pan}
        title={labels.pan}
        onClick={() => {
          onModeChange('pan');
        }}>
        <Hand />
      </Button>
      <Button
        type='button'
        variant={mode === 'draw' ? 'default' : 'outline'}
        size='icon'
        aria-label={labels.draw}
        title={labels.draw}
        onClick={() => {
          onModeChange('draw');
        }}>
        <Square />
      </Button>
      {extras}
      <Separator
        orientation='vertical'
        className='h-5'
      />
    </AnnotatorToolBarContainer>
  );
};
